import React from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Renders CHIP/Aether bot markdown with a compact, polished look.
 * Visual styling lives in ChatBot.css scoped under `.assistant-message-wrap`;
 * this map covers what CSS can't: in-app (SPA) link navigation, a
 * horizontally-scrollable table wrapper, and list-item decoration (muting a
 * trailing "(Model Family)" and giving filename rows a doc-row treatment).
 */
const FILE_RE = /\.(pdf|docx?|xlsx?|pptx?|dwg|zip|csv|txt)\b/i;
// Split a plain-text list item into { main, meta } where meta is a trailing
// "(...)" — the model-family the backend appends, e.g. "Glen_340.pdf (Glen Quart Mixer)".
const META_RE = /^(.*\S)\s*\(([^()]+)\)\s*$/;

// "ask:" links let the bot's own markdown be the clickable pick-list (e.g. the "which machine?"
// options), instead of duplicating every option as a separate SHORTCUTS chip below the message.
// Clicking one re-sends its prompt as a chat message via the chip:ask event ChatBot listens for
// (same CustomEvent pattern as chip:navigate). react-markdown strips unknown URL schemes by
// default, so ask: has to be allow-listed in urlTransform below or the href arrives empty.
const ASK_PREFIX = "ask:";
// "doc:<documentId>#page=N" source links. Deliberately NOT a pre-signed blob URL: the storage
// account is private so a link needs a SAS, and a SAS minted when the answer was written expires
// while the chat message lives on. ChatBot resolves doc: links through /documents/:id/sas-url at
// CLICK time (what the Documentation viewer already does), so they never go stale.
const DOC_PREFIX = "doc:";
const urlTransform = (url) => {
  const u = String(url || "");
  return u.startsWith(ASK_PREFIX) || u.startsWith(DOC_PREFIX) ? u : defaultUrlTransform(u);
};

// Decorate a single plain-text li: mute the trailing parenthetical, and if the
// leading text looks like a filename, render it as a monospace doc row with an icon.
const decorateLeaf = (text) => {
  const m = text.match(META_RE);
  const main = m ? m[1] : text;
  const meta = m ? m[2] : null;
  const isFile = FILE_RE.test(main);
  return (
    <span className={isFile ? "chat-md-doc" : undefined}>
      {isFile && <span className="chat-md-doc-icon" aria-hidden>📄</span>}
      <span className={isFile ? "chat-md-doc-name" : undefined}>{main}</span>
      {meta && <span className="chat-md-meta"> ({meta})</span>}
    </span>
  );
};

export default function ChatMarkdown({ children }) {
  const navigate = useNavigate();

  const components = {
    a: ({ href = "", children: kids, ...props }) => {
      if (href.startsWith(ASK_PREFIX)) {
        const prompt = decodeURIComponent(href.slice(ASK_PREFIX.length));
        return (
          <a
            href={href}
            className="chat-md-ask"
            onClick={(e) => {
              e.preventDefault();
              window.dispatchEvent(new CustomEvent("chip:ask", { detail: { prompt } }));
            }}
            {...props}
          >
            {kids}
          </a>
        );
      }
      if (href.startsWith(DOC_PREFIX)) {
        const rest = href.slice(DOC_PREFIX.length);
        const [documentId, hash] = rest.split("#");
        const page = (hash || "").startsWith("page=") ? hash.slice(5) : null;
        return (
          <a
            href={href}
            className="chat-md-doc-link"
            onClick={(e) => {
              e.preventDefault();
              // Pass the link text as the document name so the viewer has a title immediately,
              // instead of showing an empty header while it fetches. Strip the leading 📄.
              const name = String(
                Array.isArray(kids) ? kids.join("") : (typeof kids === "string" ? kids : ""),
              ).replace(/^\s*📄\s*/, "").replace(/,\s*p\.\d+\s*$/, "").trim();
              window.dispatchEvent(new CustomEvent("chip:opendoc", { detail: { documentId, page, name } }));
            }}
            {...props}
          >
            {kids}
          </a>
        );
      }
      const inApp = href.startsWith("/") && !href.startsWith("//");
      if (inApp) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              navigate(href);
            }}
            {...props}
          >
            {kids}
          </a>
        );
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {kids}
        </a>
      );
    },
    li: ({ children: kids, node, ...props }) => {
      // Unwrap the <p> remark-gfm inserts around loose-list item text, then
      // only decorate the simple single-text-node case (what CHIP's doc lists
      // are). Anything richer (bold, nested nodes) passes through untouched.
      let content = React.Children.toArray(kids);
      if (content.length === 1 && React.isValidElement(content[0]) && content[0].type === "p") {
        content = React.Children.toArray(content[0].props.children);
      }
      if (content.length === 1 && typeof content[0] === "string") {
        return <li {...props}>{decorateLeaf(content[0])}</li>;
      }
      return <li {...props}>{kids}</li>;
    },
    table: ({ children: kids }) => (
      <div className="chat-md-table-wrap">
        <table className="chat-md-table">{kids}</table>
      </div>
    ),
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      urlTransform={urlTransform}
      components={components}
    >
      {children}
    </ReactMarkdown>
  );
}
